/* eslint-disable no-restricted-properties */
import type { ReactNode } from 'react';
import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { usePersistentState } from '@vitvio/helpers';
import { getLoggerSafe } from '@vitvio/logging';
import { STORAGE_KEYS, LOCAL_FEATURE_FLAGS } from '@config/constants';
import { themes, DEFAULT_THEME, DEFAULT_THEME_DARK } from '../themes';
import type { Theme, ThemeId } from '../themes';

interface ThemeContextValue {
  currentTheme: ThemeId;
  theme: Theme;
  setTheme: (themeId: ThemeId) => void;
  availableThemes: ThemeId[];
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  currentTheme?: ThemeId;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, currentTheme }) => {
  const logger = getLoggerSafe();

  // Determine initial theme based on auto-detection feature flag
  const initialTheme = useMemo(() => {
    if (currentTheme) {
      logger?.debug('[ThemeProvider] Using explicitly provided theme', { currentTheme });
      return currentTheme;
    }

    // If auto-detection is enabled, check system preference
    if (LOCAL_FEATURE_FLAGS.enableThemeAutoDetection && typeof window !== 'undefined') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const detectedTheme = prefersDark ? DEFAULT_THEME_DARK : DEFAULT_THEME;
      logger?.debug('[ThemeProvider] Auto-detection enabled', {
        systemPreference: prefersDark ? 'dark' : 'light',
        selectedTheme: detectedTheme,
      });
      return detectedTheme;
    }

    logger?.debug('[ThemeProvider] Auto-detection disabled', { defaultTheme: DEFAULT_THEME });
    return DEFAULT_THEME;
  }, [currentTheme, logger]);

  const [themeId, setThemeId] = usePersistentState<ThemeId>(initialTheme, STORAGE_KEYS.UI_THEME);
  const availableThemes = useMemo(() => Object.keys(themes) as ThemeId[], []);

  useEffect(() => {
    if (currentTheme) {
      setThemeId(currentTheme);
    }
  }, [currentTheme, setThemeId]);

  const contextValue: ThemeContextValue = {
    currentTheme: themeId,
    theme: themes[themeId],
    setTheme: setThemeId,
    availableThemes,
  };

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
